import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { setUser } from '@/shared/sentry';
import { useUserService } from '@/shared/services';
import type { User } from '@/shared/services/user/models';
import { UserType } from '@/shared/services/user/models';

export const useUserContextStore = defineStore('userContext', () => {
  const $userService = useUserService();
  const user = ref<User>();

  async function fetchUser(force = false) {
    if (user.value && !force) {
      return Promise.resolve();
    }

    try {
      user.value = await $userService.getUserMe();
      setUser({
        id: user.value.id,
        userType: user.value.type,
        facilities: user.value.facilities.map((f) => f.id) || [],
      });

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  const userId = computed(() => user.value?.id ?? '');
  const userType = computed<UserType | undefined>(() => user.value?.type);
  const isFacilityAdmin = computed(() => userType.value === UserType.FACILITY_ADMIN);
  const isTechnicalUser = computed(() => userType.value === UserType.TECHNICAL_USER);
  const facilities = computed(() => user.value?.facilities || []);

  return {
    userId,
    userType,
    facilities,
    fetchUser,
    isFacilityAdmin,
    isTechnicalUser,
  };
});
